import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { ArrowLeft, Eye, EyeOff, Mail, Lock, User as UserIcon, Gift } from "lucide-react";

export default function Login() {
    const { user, loading, login, signup } = useAuth();
    const [params] = useSearchParams();
    const [refCode, setRefCode] = useState("");
    const navigate = useNavigate();
    const [mode, setMode] = useState("signin");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!loading && user && !submitting) navigate("/portal", { replace: true });
    }, [user, loading, navigate, submitting]);

    useEffect(() => {
        const code = params.get("ref");
        if (code) {
            setRefCode(code.toUpperCase());
            setMode("signup");
        }
    }, [params]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (mode === "signup" && password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        setSubmitting(true);
        try {
            if (mode === "signup") {
                await signup(email.trim().toLowerCase(), password, name.trim() || null, refCode || null);
                navigate("/welcome", { replace: true });
            } else {
                await login(email.trim().toLowerCase(), password);
                navigate("/portal", { replace: true });
            }
            // keep submitting=true on success — blocks the auto-redirect effect until unmount
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message || "Auth failed");
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink text-white flex flex-col bg-grain">
            <header className="px-6 md:px-12 lg:px-20 py-8 flex items-center justify-between">
                <Link to="/" data-testid="login-back" className="flex items-center gap-2 text-white/70 hover:text-rose transition">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm">Back to home</span>
                </Link>
                <div className="flex items-center gap-3">
                    <img src="/brand/24k-logo.jpg" alt="24K Glow" className="w-9 h-9 rounded-full object-cover ring-1 ring-gold/40" />
                    <span className="font-serif text-base tracking-[0.18em] uppercase"><span className="text-gold">24K</span> Glow</span>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center px-6 py-10">
                <div className="max-w-md w-full">
                    <div className="text-center">
                        <p className="text-xs tracking-[0.4em] uppercase text-rose">✦ Members access ✦</p>
                        <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-tight">
                            {mode === "signup" ? <>Welcome to your <span className="italic text-rose">glow.</span></> : <>Welcome <span className="italic text-rose">back.</span></>}
                        </h1>
                        <p className="mt-5 text-white/60 leading-relaxed">
                            {mode === "signup"
                                ? "Create your account to subscribe, browse past designs, and book same-day appointments."
                                : "Sign in to manage your subscription, gallery, and bookings."}
                        </p>
                    </div>

                    {/* Tabs */}
                    <div className="mt-8 grid grid-cols-2 gap-1 rounded-full p-1 bg-surface border border-white/10">
                        <button
                            type="button"
                            data-testid="tab-signin"
                            onClick={() => { setMode("signin"); setError(""); }}
                            className={`py-2 rounded-full text-sm transition ${mode === "signin" ? "bg-rose text-ink font-medium" : "text-white/60"}`}
                        >
                            Sign in
                        </button>
                        <button
                            type="button"
                            data-testid="tab-signup"
                            onClick={() => { setMode("signup"); setError(""); }}
                            className={`py-2 rounded-full text-sm transition ${mode === "signup" ? "bg-rose text-ink font-medium" : "text-white/60"}`}
                        >
                            Create account
                        </button>
                    </div>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        {mode === "signup" && refCode && (
                            <div data-testid="auth-ref-badge" className="rounded-2xl border border-gold/40 bg-gold/10 p-3 flex items-center gap-3">
                                <Gift className="w-4 h-4 text-gold" />
                                <p className="text-sm">
                                    Referral code <span className="font-mono text-gold">{refCode}</span> applied — your friend's gift of <span className="text-gold">$55 off your first set</span> will apply at checkout.
                                </p>
                            </div>
                        )}
                        {mode === "signup" && (
                            <div className="relative">
                                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                <input
                                    type="text"
                                    placeholder="Your name"
                                    data-testid="auth-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    autoComplete="name"
                                    className="w-full bg-surface border border-white/10 focus:border-rose rounded-2xl pl-11 pr-4 py-3.5 outline-none transition"
                                />
                            </div>
                        )}
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                            <input
                                type="email"
                                placeholder="Email"
                                data-testid="auth-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                required
                                className="w-full bg-surface border border-white/10 focus:border-rose rounded-2xl pl-11 pr-4 py-3.5 outline-none transition"
                            />
                        </div>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                            <input
                                type={showPw ? "text" : "password"}
                                placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
                                data-testid="auth-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                                required
                                minLength={mode === "signup" ? 8 : undefined}
                                className="w-full bg-surface border border-white/10 focus:border-rose rounded-2xl pl-11 pr-12 py-3.5 outline-none transition"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                data-testid="auth-toggle-pw"
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-white/40 hover:text-white transition"
                                aria-label={showPw ? "Hide password" : "Show password"}
                            >
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>

                        {error && (
                            <p data-testid="auth-error" className="text-sm text-rose bg-rose/10 border border-rose/30 rounded-xl px-4 py-3">
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            data-testid="auth-submit"
                            disabled={submitting}
                            className="w-full px-7 py-4 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium disabled:opacity-50"
                        >
                            {submitting ? "Working…" : mode === "signup" ? "Create my glow account" : "Sign in"}
                        </button>
                    </form>

                    <div className="mt-6 flex items-center gap-4">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-xs text-white/40 uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    <button
                        type="button"
                        data-testid="google-signin-btn"
                        onClick={() => {
                            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                            const redirectUrl = window.location.origin + "/portal";
                            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                        }}
                        className="mt-6 w-full px-7 py-4 rounded-full border border-white/20 hover:border-gold/60 hover:bg-white/5 transition font-medium flex items-center justify-center gap-3"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continue with Google
                    </button>

                    <p className="mt-6 text-xs text-white/40 tracking-wide text-center">
                        {mode === "signup"
                            ? "By creating an account, you agree to receive booking confirmations and studio updates. We never share your data."
                            : "Trouble signing in? Message the studio from the home page."}
                    </p>
                </div>
            </main>
        </div>
    );
}
